import { useRecoilState, useSetRecoilState } from 'recoil';
import { navigationFinishScreenState, navigationSettingsState, sliderValueState } from 'store';
import Image from 'next/image';
import { Stack, Typography, ClickAwayListener } from '@mui/material';
import { useEffect, useState } from 'react';
import { Loader } from '@treedis/tour-loader';
import { NavigationBackground } from './NavigationBackground';
import { NavigationHeader } from './NavigationHeader';
import { NavigationSearchBar } from './search-bar/NavigationSearchBar';
import { NavigationCategories } from './NavigationCategories';
import { getNavigationData } from '@/api/queries';
import { TourType } from '@/_models/Tour';
import { getQueryVariable } from '../util';
import { randomNumberId } from '@/utils/randomNumberId';
import { TeleportOverlay } from './TeleportOverlay';

type NavigationWrapperProps = {
  isBackgroundVisible: boolean;
  setBackgroundVisible: (val: boolean) => void;
  setDestination: (val: any, navigationSettings: any, setSliderValue: any) => void;
  actionPathFinished: boolean;
  tour: any;
  targetLocation: any;
  moveToStartingPoint: () => Promise<void>;
  tourType: TourType;
  appState?: string;
  setActionPathFinished: (val: boolean) => void;
  pathFinderLine: any;
  rtl: boolean;
  showTeleportLoading: boolean;
  setShowTeleportLoading: (val: boolean) => void;
  endTeleportName: string;
};

export const NavigationWrapper = ({
  isBackgroundVisible,
  setBackgroundVisible,
  setDestination,
  actionPathFinished,
  tour,
  targetLocation,
  moveToStartingPoint,
  tourType,
  appState,
  setActionPathFinished,
  pathFinderLine,
  rtl,
  showTeleportLoading,
  setShowTeleportLoading,
  endTeleportName,
}: NavigationWrapperProps) => {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [navigationItems, setNavigationItems] = useState([]);
  const [navigationCategories, setNavigationCategories] = useState([]);
  const [navigationSettings, setNavigationSettings] = useRecoilState(navigationSettingsState);
  const [navigationFinishScreen, setNavigationFinishScreen] = useRecoilState(navigationFinishScreenState);
  const { filtersEnabled, sortingEnabled, defaultSorting } = navigationSettings;
  const [isSearchBarFocused, setIsSearchBarFocused] = useState(false);
  const [isTagsOverlayHidden, setIsTagsOverlayHidden] = useState(false);
  const setSliderValue = useSetRecoilState(sliderValueState);

  useEffect(() => {
    async function fetchData() {
      // You can await here
      const {
        navigationItems: fetchedItems,
        navigationCategories: fetchedCategories,
        navigationFinishScreen: fetchedFinishScreen,
        navigationSettings: fetchedSettings,
      } = await getNavigationData(tour.id);

      const otherCategoryId = randomNumberId();

      const itemsWithoutCategory = fetchedItems.filter((item: any) => !item.category || !item.categoryId);
      setNavigationItems(fetchedItems.map((item) => ({ ...item, categoryId: item.categoryId || otherCategoryId })));
      setNavigationCategories([
        ...fetchedCategories.filter((category) => category.items.length),
        { id: otherCategoryId, title: rtl ? 'אחר' : 'Other', items: itemsWithoutCategory },
      ]);
      if (fetchedSettings) setNavigationSettings(fetchedSettings);
      if (fetchedFinishScreen) setNavigationFinishScreen(fetchedFinishScreen);
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tour.id]);

  const selectCategory = (category: any) => {
    setSelectedCategory(category);
  };

  useEffect(() => {
    const target = getQueryVariable(window.location.href, 'target');
    // this is for loading in mobile from qr or sms link
    if (appState === 'playing' && !targetLocation && target && tourType === TourType.Regular && pathFinderLine) {
      setDestination({ sid: target }, navigationSettings, setSliderValue);
      setBackgroundVisible(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appState, targetLocation, pathFinderLine]);

  useEffect(() => {
    if (isBackgroundVisible && pathFinderLine) {
      pathFinderLine.inAutoNavigate = false;
    }
  }, [isBackgroundVisible, pathFinderLine]);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    // Function to find and remove the tags overlay
    const findAndRemoveTagsOverlay = () => {
      const iframe = document.body.querySelector('iframe');
      const tagsNavigationOverlay = iframe?.contentWindow?.document.body.querySelector('.tags-overlay');
      if (tagsNavigationOverlay) {
        tagsNavigationOverlay.remove();
        setIsTagsOverlayHidden(true); // Update state to indicate the overlay is hidden
        return true; // Overlay found and removed
      }
      return false; // Overlay not found
    };

    if (!isTagsOverlayHidden) {
      const interval = setInterval(() => {
        const foundAndRemoved = findAndRemoveTagsOverlay();
        if (foundAndRemoved) {
          clearInterval(interval); // Stop the polling if the overlay is found and removed
        }
      }, 1000); // Check every 1000 milliseconds (1 second)

      return () => clearInterval(interval); // Cleanup function to clear interval when the component unmounts or variables change
    }
  }, [isTagsOverlayHidden]);

  return (
    <>
      {showTeleportLoading ? (
        <TeleportOverlay setShowTeleportLoading={setShowTeleportLoading} endTeleportName={endTeleportName} />
      ) : (
        <>
          <NavigationBackground isBackgroundVisible={isBackgroundVisible} />
          <Stack
            sx={{
              position: 'absolute',
              width: '100%',
              height: '100vh',
              background: 'transparent',
              zIndex: 1200,
              top: 0,
              pointerEvents: isBackgroundVisible ? 'auto' : 'none !important',
              direction: rtl ? 'rtl' : 'ltr',
            }}
          >
            <NavigationHeader rtl={rtl} isBackgroundVisible={isBackgroundVisible} tour={tour} tourType={tourType} />

            <ClickAwayListener
              onClickAway={() => {
                setIsSearchBarFocused(false);
                setSelectedCategory(null);
              }}
            >
              <Stack
                role="presentation"
                sx={{
                  width: 'fit-content',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: '76px',
                  alignSelf: 'center',
                }}
              >
                <NavigationSearchBar
                  appState={appState}
                  isBackgroundVisible={isBackgroundVisible}
                  menuItems={navigationItems}
                  setDestination={setDestination}
                  setBackgroundVisible={(val: boolean) => setBackgroundVisible(val)}
                  actionPathFinished={actionPathFinished}
                  selectedCategory={selectedCategory}
                  tour={tour}
                  target={targetLocation}
                  selectCategory={selectCategory}
                  categories={navigationCategories}
                  moveToStartingPoint={moveToStartingPoint}
                  filtersEnabled={filtersEnabled}
                  sortingEnabled={sortingEnabled}
                  defaultSorting={defaultSorting}
                  navigationFinishScreen={navigationFinishScreen}
                  tourType={tourType}
                  setActionPathFinished={setActionPathFinished}
                  isSearchBarFocused={isSearchBarFocused}
                  setIsSearchBarFocused={setIsSearchBarFocused}
                  rtl={rtl}
                  inAutoNavigate={pathFinderLine?.inAutoNavigate}
                />
                {isBackgroundVisible && navigationCategories?.length > 1 && (
                  <Stack width="100%" alignItems="center" justifyContent="center">
                    {appState === 'playing' ? (
                      <NavigationCategories rtl={rtl} categories={navigationCategories} handleSelectCategory={selectCategory} />
                    ) : (
                      <Loader color="#2055FF" />
                    )}
                  </Stack>
                )}
              </Stack>
            </ClickAwayListener>

            {isBackgroundVisible && (
              <Stack direction="row" gap={2} alignItems="center" justifyContent="center" sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, mx: 'auto', mb: '20px' }}>
                <Typography fontSize="20px" fontWeight={400}>
                  {rtl ? 'מופעל על ידי' : 'Powered by'}
                </Typography>
                <Image src="/logo-black.svg" width="117px" height="42px" />
              </Stack>
            )}
          </Stack>
        </>
      )}
    </>
  );
};
